import React, { useState, useEffect, useCallback } from 'react';
import { Repeat, DollarSign, Loader2, AlertTriangle, Check, X, Package } from 'lucide-react';
import { api, useAuth, formatApiErrorDetail } from '../App';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

export default function TradeProposalDialog({ open, onOpenChange, listing }) {
  const { user } = useAuth();
  const [myListings, setMyListings] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [cashAmount, setCashAmount] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchMyListings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/my-listings?status=active');
      setMyListings(data.filter(l => l.id !== listing?.id));
    } catch {
      // silently handled
    } finally {
      setLoading(false);
    }
  }, [listing?.id]);

  useEffect(() => {
    if (open) {
      fetchMyListings();
      setSelectedIds([]);
      setCashAmount('');
      setMessage('');
    }
  }, [open, fetchMyListings]);

  const toggleListing = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedIds.length === 0 && !cashAmount) {
      toast.error('Select at least one item or add cash to offer');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/trades', {
        listing_id: listing.id,
        offered_listing_ids: selectedIds,
        cash_amount: cashAmount ? parseFloat(cashAmount) : 0,
        message: message || null,
        expires_in_days: 7
      });
      toast.success('Trade proposal sent!');
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedTotal = myListings
    .filter(l => selectedIds.includes(l.id))
    .reduce((sum, l) => sum + (l.price || 0), 0) + (parseFloat(cashAmount) || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface-card border-border-subtle max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-clash text-xl text-white flex items-center gap-2">
            <Repeat className="w-5 h-5 text-gold" /> Propose a Trade
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            Select items from your listings to offer in exchange
          </DialogDescription>
        </DialogHeader>

        {/* Warning */}
        <div className="flex items-start gap-3 p-3 bg-yellow-500/10 border border-yellow-500/20 mt-2">
          <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-yellow-200">
            Review the seller's item details, photos, and condition before proposing. Trades are binding once both parties confirm.
          </p>
        </div>

        {/* Target listing */}
        <div className="p-4 bg-surface-elevated border border-border-subtle mt-4">
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">You want</p>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 bg-surface-base flex-shrink-0 overflow-hidden">
              {listing?.images?.[0] ? (
                <img src={listing.images[0].startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${listing.images[0]}` : listing.images[0]} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><Package className="w-6 h-6 text-zinc-600" /></div>
              )}
            </div>
            <div>
              <p className="font-medium text-white">{listing?.title}</p>
              <p className="text-sm text-gold font-clash font-semibold">${listing?.price?.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Select items */}
          <div>
            <p className="text-sm font-medium text-zinc-300 mb-3">Select items to offer</p>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-gold animate-spin" />
              </div>
            ) : myListings.length > 0 ? (
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {myListings.map(item => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => toggleListing(item.id)}
                    data-testid={`trade-select-${item.id}`}
                    className={`w-full p-3 flex items-center gap-3 text-left transition-colors border ${
                      selectedIds.includes(item.id)
                        ? 'bg-gold/10 border-gold'
                        : 'bg-surface-base border-border-subtle hover:border-zinc-600'
                    }`}
                  >
                    <div className="w-10 h-10 bg-surface-elevated flex-shrink-0 overflow-hidden">
                      {item.images?.[0] ? (
                        <img src={item.images[0].startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${item.images[0]}` : item.images[0]} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Package className="w-5 h-5 text-zinc-600 m-auto mt-2.5" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{item.title}</p>
                      <p className="text-xs text-zinc-400">${item.price?.toLocaleString()}</p>
                    </div>
                    {selectedIds.includes(item.id) ? (
                      <Check className="w-5 h-5 text-gold flex-shrink-0" />
                    ) : (
                      <div className="w-5 h-5 border border-zinc-600 flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-zinc-400 text-sm">
                You need active listings to propose a trade.{' '}
                <a href="/create-listing" className="text-gold hover:underline">Create one</a>
              </div>
            )}
          </div>

          {/* Cash amount */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              <DollarSign className="w-4 h-4 inline mr-1" />Add cash (optional)
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
              <input
                type="number"
                value={cashAmount}
                onChange={(e) => setCashAmount(e.target.value)}
                placeholder="0.00"
                className="input-dark w-full pl-8 pr-4 py-3"
                data-testid="trade-cash-input"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">Message (optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a note to the seller..."
              className="input-dark w-full px-4 py-3 h-20 resize-none"
              data-testid="trade-message-input"
            />
          </div>

          {/* Summary */}
          {(selectedIds.length > 0 || cashAmount) && (
            <div className="p-4 bg-gold/5 border border-gold/20">
              <p className="text-sm text-zinc-300">
                <span className="text-gold font-semibold">Your offer:</span>{' '}
                {selectedIds.length > 0 && `${selectedIds.length} item(s)`}
                {selectedIds.length > 0 && cashAmount && ' + '}
                {cashAmount && `$${parseFloat(cashAmount).toLocaleString()} cash`}
                {' '}(~${selectedTotal.toLocaleString()} total value)
              </p>
              <p className="text-xs text-zinc-500 mt-1">Expires in 7 days</p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="btn-secondary flex-1 py-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || (selectedIds.length === 0 && !cashAmount)}
              className="btn-gold flex-1 py-3 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="submit-trade-btn"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Proposal'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
